<template>
  <div class="inhoud">
    <draggable
      :list="chapters"
      tag="ol"
      class="inhoud__chapters"
      group="inhoud-chapters"
      item-key="uid"
      handle=".inhoud__chapter-grip"
      :disabled="readonly"
      @end="$emit('changed')"
    >
      <template #item="{ element: chapter, index: ci }">
        <li class="inhoud__chapter">
          <header class="inhoud__chapter-head">
            <span
              v-if="!readonly"
              class="inhoud__grip inhoud__chapter-grip"
              title="Drag to reorder chapter"
              >⋮⋮</span
            >
            <button
              type="button"
              class="inhoud__chapter-toggle"
              :aria-expanded="!isCollapsed(chapter)"
              @click="toggleChapter(chapter)"
            >
              <span class="inhoud__seq">{{ pad(ci + 1) }}</span>
              <span class="inhoud__chapter-title">{{
                chapter.title || "Untitled chapter"
              }}</span>
            </button>
            <span class="inhoud__count">{{
              (chapter.insights || []).length
            }}</span>
            <button
              v-if="!readonly"
              type="button"
              class="inhoud__chapter-x"
              :title="`Delete ${chapter.title || 'this chapter'}`"
              @click="$emit('remove-chapter', ci)"
            >
              ×
            </button>
            <button
              type="button"
              class="inhoud__chev"
              :class="{ 'inhoud__chev--open': !isCollapsed(chapter) }"
              :aria-label="isCollapsed(chapter) ? 'Expand' : 'Collapse'"
              @click="toggleChapter(chapter)"
            >
              <Icon name="chevron-down" :size="12" />
            </button>
          </header>

          <draggable
            v-show="!isCollapsed(chapter)"
            :list="chapter.insights"
            tag="ol"
            class="inhoud__rows"
            group="inhoud-insights"
            item-key="uid"
            handle=".inhoud__row-grip"
            :disabled="readonly"
            @end="$emit('changed')"
          >
            <template #item="{ element: insight, index: ii }">
              <li
                class="inhoud__row"
                :class="`inhoud__row--${insight.status || 'draft'}`"
                tabindex="0"
                @click="open(ci, ii, insight)"
                @keydown.enter.prevent="open(ci, ii, insight)"
              >
                <span
                  class="inhoud__row-mark"
                  :title="statusLabel(insight.status)"
                />
                <span class="inhoud__row-label" :title="insight.title || ''">{{
                  insight.topic || insight.title || "Untitled insight"
                }}</span>
                <span class="inhoud__row-actions">
                  <button
                    type="button"
                    class="inhoud__row-btn"
                    :class="{
                      'inhoud__row-btn--done': copiedKey === insight.uid,
                    }"
                    :title="
                      copiedKey === insight.uid ? 'Copied' : 'Copy insight'
                    "
                    @click.stop="copyInsight(insight)"
                  >
                    <Icon name="copy" :size="12" />
                  </button>
                  <span
                    v-if="!readonly"
                    class="inhoud__grip inhoud__row-grip"
                    title="Drag to reorder"
                    @click.stop
                    >⋮⋮</span
                  >
                </span>
              </li>
            </template>
          </draggable>
          <p
            v-if="!isCollapsed(chapter) && !(chapter.insights || []).length"
            class="inhoud__chapter-empty"
          >
            No insights yet
          </p>
        </li>
      </template>
    </draggable>

    <div v-if="!chapters.length" class="inhoud__empty">
      <slot name="empty">No chapters yet.</slot>
    </div>
  </div>
</template>

<script>
import draggable from "vuedraggable";
import Icon from "@/components/alice/Icon.vue";
import { copyFeedbackMixin } from "@/utils/clipboard";
import { insightToClipboardText } from "@/utils/insightsMarkdown";
import { INSIGHT_STATUS_LABELS, normalizeStatus } from "@/utils/insightsModel";

// "Inhoud": the compact table of contents of the report — numbered chapters,
// one row per insight showing its topic. Drag handles reorder chapters and
// move insights within/across chapters; the parent persists on `changed`.
// Mutates `chapters` in place (vuedraggable `list`), like the Insights page.
export default {
  name: "InsightsContentsPanel",
  components: { draggable, Icon },
  mixins: [copyFeedbackMixin],
  props: {
    chapters: { type: Array, default: () => [] },
    readonly: { type: Boolean, default: false },
  },
  emits: ["open", "remove-chapter", "changed"],
  data() {
    return { collapsed: {} };
  },
  methods: {
    pad(n) {
      return String(n).padStart(2, "0");
    },
    statusLabel(status) {
      return INSIGHT_STATUS_LABELS[normalizeStatus(status)];
    },
    isCollapsed(chapter) {
      return !!this.collapsed[chapter.uid];
    },
    toggleChapter(chapter) {
      if (!chapter.uid) return;
      this.collapsed = {
        ...this.collapsed,
        [chapter.uid]: !this.collapsed[chapter.uid],
      };
    },
    open(chapterIndex, insightIndex, insight) {
      this.$emit("open", { chapterIndex, insightIndex, insight });
    },
    copyInsight(insight) {
      this.copyWithFeedback(insight.uid, insightToClipboardText(insight));
    },
  },
};
</script>

<style scoped lang="scss">
.inhoud {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.inhoud__chapters,
.inhoud__rows {
  list-style: none;
  margin: 0;
  padding: 0;
}
.inhoud__chapters {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.inhoud__chapter {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.inhoud__chapter-head {
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 28px;
}
.inhoud__grip {
  font-family: var(--font-mono);
  font-size: 12px;
  line-height: 1;
  color: var(--fg-3);
  cursor: grab;
  user-select: none;
  flex-shrink: 0;
}
.inhoud__grip:active {
  cursor: grabbing;
}
.inhoud__chapter-grip {
  opacity: 0;
  transition: opacity 0.12s ease;
}
.inhoud__chapter-head:hover .inhoud__chapter-grip {
  opacity: 1;
}
.inhoud__chapter-toggle {
  flex: 1;
  min-width: 0;
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
  padding: 2px 0;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
}
.inhoud__seq {
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 700;
  color: var(--fg-3);
  flex-shrink: 0;
}
.inhoud__chapter-title {
  min-width: 0;
  font-family: var(--font-display);
  font-size: 15px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--vp-black);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.inhoud__count {
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  color: var(--fg-3);
  flex-shrink: 0;
}
.inhoud__chapter-x,
.inhoud__chev {
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 0;
  cursor: pointer;
  color: var(--fg-3);
  flex-shrink: 0;
  transition: color 0.12s ease, border-color 0.12s ease, transform 0.15s ease;
}
.inhoud__chapter-x {
  font-family: var(--font-display);
  font-size: 16px;
  line-height: 1;
  opacity: 0;
}
.inhoud__chapter-head:hover .inhoud__chapter-x {
  opacity: 1;
}
.inhoud__chapter-x:hover {
  color: var(--vp-rood-100);
  border-color: var(--vp-rood-100);
}
.inhoud__chev:hover {
  color: var(--vp-black);
}
.inhoud__chev--open {
  transform: rotate(180deg);
}

.inhoud__rows {
  display: flex;
  flex-direction: column;
  min-height: 12px;
}
.inhoud__row {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 34px;
  padding: 6px 6px 6px 12px;
  border-bottom: 1px solid var(--border-hairline);
  cursor: pointer;
  outline: none;
  transition: background 0.12s ease;
}
.inhoud__row:first-child {
  border-top: 1px solid var(--border-hairline);
}
.inhoud__row:hover,
.inhoud__row:focus-visible {
  background: var(--bg-muted);
}
.inhoud__row-mark {
  position: absolute;
  left: 0;
  top: 6px;
  bottom: 6px;
  width: 3px;
  background: var(--vp-black);
}
/* Report status: Klad black, Ter review yellow, Klaar green. */
.inhoud__row--review .inhoud__row-mark {
  background: var(--vp-geel-100);
}
.inhoud__row--done .inhoud__row-mark {
  background: var(--vp-groen-100);
}
.inhoud__row-label {
  flex: 1;
  min-width: 0;
  font-family: var(--font-body);
  font-size: 13px;
  font-weight: 600;
  line-height: 1.35;
  color: var(--vp-black);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.inhoud__row-actions {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 0.12s ease;
}
.inhoud__row:hover .inhoud__row-actions,
.inhoud__row:focus-visible .inhoud__row-actions,
.inhoud__row-actions:focus-within {
  opacity: 1;
}
.inhoud__row-btn {
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 0;
  cursor: pointer;
  color: var(--fg-3);
  transition: color 0.12s ease, border-color 0.12s ease;
}
.inhoud__row-btn:hover {
  color: var(--vp-black);
  border-color: var(--vp-black);
}
.inhoud__row-btn--done {
  color: var(--vp-turquoise-100);
  border-color: var(--vp-turquoise-100);
}
.inhoud__chapter-empty {
  margin: 0;
  padding: 6px 12px;
  font-family: var(--font-body);
  font-size: 12px;
  color: var(--fg-3);
}
.inhoud__empty {
  padding: 16px;
  border: 1px dashed var(--border-hairline);
  font-family: var(--font-body);
  font-size: 13px;
  color: var(--fg-3);
  background: var(--vp-white);
}
/* Sortable ghost while dragging */
.inhoud :deep(.sortable-ghost) {
  opacity: 0.4;
}
</style>
